import type { Database } from "@/integrations/supabase/types";

type PublicProperty = Pick<
  Database["public"]["Tables"]["properties"]["Row"],
  | "id"
  | "title"
  | "locality"
  | "city"
  | "address"
  | "price"
  | "price_min"
  | "price_max"
  | "property_type"
  | "area_sqft"
  | "area_min"
  | "area_max"
  | "bedrooms"
  | "bedrooms_options"
  | "bathrooms"
  | "images"
  | "description"
  | "is_featured"
  | "latitude"
  | "longitude"
  | "developer_name"
  | "created_at"
  | "slug"
  | "project_name"
  | "listing_type"
  | "status"
> & { price_on_request?: boolean | null };

// Hardcoded to avoid any env-var or SDK initialization issues
const SUPABASE_URL = "https://fmhokzqxpqgficjyzvnr.supabase.co";
const SUPABASE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZtaG9renF4cHFnZmljanl6dm5yIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjgyMzUwNzYsImV4cCI6MjA4MzgxMTA3Nn0.s-YDzTAa7hI4SumjEuTn6HRAaDNWep-U60E8QXTy7SE";

const PUBLIC_PROPERTIES_SELECT =
  "id,title,locality,city,address,price,price_on_request,price_min,price_max,property_type,listing_type,area_sqft,area_min,area_max,bedrooms,bedrooms_options,bathrooms,images,description,is_featured,latitude,longitude,developer_name,created_at,slug,project_name,possession_date,possession_stage,status";

/**
 * Uses XMLHttpRequest instead of fetch() to bypass the lovable.js
 * fetch wrapper that intercepts and breaks window.fetch calls in preview.
 */
const xhrGet = (url: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("GET", url, true);
    xhr.setRequestHeader("apikey", SUPABASE_KEY);
    xhr.setRequestHeader("Authorization", `Bearer ${SUPABASE_KEY}`);
    xhr.setRequestHeader("Accept", "application/json");
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.responseText);
      } else {
        reject(new Error(`XHR failed (${xhr.status}): ${xhr.statusText}`));
      }
    };
    xhr.onerror = () => reject(new Error("XHR network error"));
    xhr.ontimeout = () => reject(new Error("XHR timeout"));
    xhr.timeout = 15000;
    xhr.send();
  });

export const fetchPublishedPropertiesWithoutPreflight = async (): Promise<PublicProperty[]> => {
  const url = new URL(`${SUPABASE_URL}/rest/v1/properties`);
  url.searchParams.set("select", PUBLIC_PROPERTIES_SELECT);
  url.searchParams.set("is_published", "eq.true");
  url.searchParams.set("status", "eq.active");
  url.searchParams.set("order", "is_featured.desc,created_at.desc");

  const responseText = await xhrGet(url.toString());
  const data = JSON.parse(responseText);
  return Array.isArray(data) ? (data as any[]).filter(p => !p.deleted_at) as PublicProperty[] : [];
};
